import React from 'react'
import cx from 'classnames'
import { formatPrice } from 'helpers'

import { CheckedList, Label, ProductCountImage, Text } from 'components/dataDisplay'
import { Button } from 'components/inputs'
import ProductPrice from 'compositions/ProductPrice/ProductPrice'

import messages from './messages'
import s from './PlanCard.scss'


type PlanCardProps = {
  className?: string
  data: SubscriptionModule.UpgradePlan
  onClick?: (plan: SubscriptionModule.UpgradePlan) => void
}

const PlanCard: React.FC<PlanCardProps> = ({ className, data, onClick }) => {
  const { productsPerPeriod, price: planPrice, percentageDiscount: percent, save, discount } = data
  const discountPlanPriceUSD = discount?.defaultDiscountPrice ? discount?.defaultDiscountPrice / 100 : null
  const isCouponDiscountExists = discountPlanPriceUSD && discountPlanPriceUSD < planPrice / 100

  const handleClick = () => {
    if (typeof onClick === 'function') {
      onClick(data)
    }
  }

  const isOnePerMonth = productsPerPeriod === 1
  const isMostPopular = productsPerPeriod === 2

  const price = formatPrice(planPrice)
  const savePrice = formatPrice(save)

  const checkedListItems: Intl.Message[] = isOnePerMonth
    ? [ messages.oneBottle ]
    : [ { ...messages.saveLong, values: { percent, save: savePrice } } ]

  if (isCouponDiscountExists) {
    checkedListItems.push({ ...messages.renews, values: { price } })
  }

  const rootClassName = cx(
    className,
    isMostPopular && s.highlight,
    'relative pt-32 pb-24 px-24 h-full flex flex-col items-start justify-start bg-white text-left'
  )

  const title = { ...messages.subscriptionTitle, values: { productsPerPeriod } }
  const buttonTitle = { ...messages.choose, values: { count: productsPerPeriod } }

  return (
    <div className={rootClassName}>
      {
        isMostPopular && (
          <Label
            className={s.mostPopularLabel}
            title={messages.mostPopularLabel}
            bgColor="gold-30"
            textColor="black"
            direction="right"
            absolute
            withTail
          />
        )
      }
      <div className="flex">
        <ProductCountImage
          className={s.countImageWrapper}
          imageClassName={s.countImage}
          count={productsPerPeriod}
          imageType="flask"
        />
        <div>
          <Text
            className="whitespace-nowrap "
            message={title}
            style="sh5"
            data-testid={`subscriptionPlanProductsPerPeriod_${productsPerPeriod}`}
          />
          <>
            <ProductPrice
              className="mt-8 inline-flex flex-row-reverse items-baseline"
              data={{
                standardPrice: { amount: planPrice / 100, currencyCode: 'USD' },
                discountPrice: { amount: discountPlanPriceUSD, currencyCode: 'USD' },
              }}
              textStyle="h3"
              oldPriceClassName={s.oldPrice}
            />
            {
              isCouponDiscountExists && (
                <Text
                  className="mt-8"
                  message={messages.forFirstMonth}
                  style="sh6"
                  color="gold-50"
                />
              )
            }
          </>
          <CheckedList
            className="mt-16"
            items={checkedListItems}
            gap={8}
          />
        </div>
      </div>
      <Button
        className="mt-16"
        title={buttonTitle}
        size={56}
        fullWidth
        inlineIcon="32/arrow-right"
        onClick={handleClick}
        data-testid="subscriptionPlanAction"
      />
    </div>
  )
}


export default React.memo(PlanCard)
